import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import {
  BbWithdrawalEntry,
  bbGetPendingWithdrawals,
  bbGetWithdrawals,
  bbProcessWithdrawal,
  bbRejectWithdrawal,
} from '../../../services/blindBoxService';
import { PermissionButton } from '../../../../../routes/components/permission-button';
import dayjs from 'dayjs';

export function SectionWithdrawals({ refreshKey }: { refreshKey: number }) {
  const t = useTranslate();
  const notify = useNotify();
  const [rows, setRows] = useState<BbWithdrawalEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [listMode, setListMode] = useState<'all' | 'pending'>('all');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(80);
  const [approveDlg, setApproveDlg] = useState<{ id: string } | null>(null);
  const [rejDlg, setRejDlg] = useState<{ id: string } | null>(null);
  const [reason, setReason] = useState('');

  const load = useCallback(() => {
    const p = { limit: rowsPerPage, offset: page * rowsPerPage };
    if (listMode === 'pending' && !status) {
      bbGetPendingWithdrawals(p.limit, p.offset).then((r) => {
        setRows(r.entries || []);
        setTotal(r.total || 0);
      });
    } else {
      bbGetWithdrawals({ ...p, status: status || undefined }).then((r) => {
        setRows(r.entries || []);
        setTotal(r.total || 0);
      });
    }
  }, [listMode, status, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [listMode, status]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const approveWithdrawal = async () => {
    if (!approveDlg) return;
    try {
      await bbProcessWithdrawal(approveDlg.id);
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      setApproveDlg(null);
      load();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    }
  };

  const rejectW = async () => {
    if (!rejDlg) return;
    try {
      await bbRejectWithdrawal(rejDlg.id, reason);
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      setRejDlg(null);
      setReason('');
      load();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={listMode}
          onChange={(_, v) => v && setListMode(v)}
        >
          <ToggleButton value="all">{t('module.blindBox.withdrawListAll')}</ToggleButton>
          <ToggleButton value="pending">{t('module.blindBox.withdrawListPending')}</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          select
          size="small"
          label="status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">(all)</MenuItem>
          <MenuItem value="PENDING">PENDING</MenuItem>
          <MenuItem value="UNDER_REVIEW">UNDER_REVIEW</MenuItem>
          <MenuItem value="PROCESSING">PROCESSING</MenuItem>
          <MenuItem value="DISTRIBUTED">DISTRIBUTED</MenuItem>
          <MenuItem value="FAILED">FAILED</MenuItem>
          <MenuItem value="REJECTED">REJECTED</MenuItem>
        </TextField>
        <Button size="small" variant="outlined" onClick={load}>
          {t('module.blindBox.refresh')}
        </Button>
      </Stack>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>userId</TableCell>
              <TableCell>address</TableCell>
              <TableCell>token</TableCell>
              <TableCell>amount</TableCell>
              <TableCell>status</TableCell>
              <TableCell>time</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((w) => (
              <TableRow key={w.id}>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <span>{w.userId}</span>
                    {w.isBanned ? (
                      <Chip size="small" color="error" label={t('module.blindBox.lbBannedTag')} />
                    ) : null}
                  </Stack>
                </TableCell>
                <TableCell>{w.walletAddress}</TableCell>
                <TableCell>{w.tokenType}</TableCell>
                <TableCell>{w.amount}</TableCell>
                <TableCell>{w.status}</TableCell>
                <TableCell>
                  {w.requestedAt ? dayjs(w.requestedAt).format('YYYY-MM-DD HH:mm:ss') : '—'}
                </TableCell>
                <TableCell align="right">
                  <PermissionButton resource="bb-withdrawals" action="update">
                    <Tooltip
                      title={w.isBanned ? t('module.blindBox.withdrawBannedApproveHint') : ''}
                      arrow
                    >
                      {/* span keeps the tooltip working on the disabled button */}
                      <span>
                        <Button
                          size="small"
                          disabled={!!w.isBanned}
                          onClick={() => setApproveDlg({ id: w.id })}
                        >
                          {t('module.blindBox.approve')}
                        </Button>
                      </span>
                    </Tooltip>
                    <Button size="small" onClick={() => setRejDlg({ id: w.id })}>
                      {t('module.blindBox.reject')}
                    </Button>
                  </PermissionButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          const next = parseInt(e.target.value, 10);
          setRowsPerPage(Number.isFinite(next) && next > 0 ? next : 80);
          setPage(0);
        }}
        rowsPerPageOptions={[20, 50, 80, 100]}
      />

      <Dialog open={!!approveDlg} onClose={() => setApproveDlg(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('module.blindBox.approveWithdrawalTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>{t('module.blindBox.confirmApproveWithdrawal')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDlg(null)}>{t('actions.cancel')}</Button>
          <PermissionButton resource="bb-withdrawals" action="update">
            <Button variant="contained" color="success" onClick={approveWithdrawal}>
              {t('module.blindBox.approve')}
            </Button>
          </PermissionButton>
        </DialogActions>
      </Dialog>

      <Dialog open={!!rejDlg} onClose={() => setRejDlg(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('module.blindBox.rejectReason')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejDlg(null)}>{t('actions.cancel')}</Button>
          <PermissionButton resource="bb-withdrawals" action="update">
            <Button variant="contained" color="error" onClick={rejectW}>
              {t('module.blindBox.reject')}
            </Button>
          </PermissionButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
